import { T } from '@territory/tokens';
import { font, radius, space, TOUCH_MIN, weight } from '../scale.js';
import { RECON_HOVER_MS, RECON_PRESS_MS } from './reconRoles.js';

/* These roles are exact decorative washes, card fills and pressed surfaces in products.recon.
   The variants preserve the shipped off-scale dimensions and their native browser defaults. */
const FALLBACKS = { card: T.surface, surface3: T.surfaceAlt, white: T.onAccent, black: T.bg, 'wash-emerald-32': T.accentSoft, 'wash-emerald-05': T.accentSoft, 'wash-emerald-12': T.accentSoft, 'wash-emerald-16': T.accentSoft } as const;
const role = (name: keyof typeof FALLBACKS): string => `var(--rv-recon-${name}, ${FALLBACKS[name]})`;
/* Shipped CTA dimensions outside the base scales. */
const RECON_GOOGLE_H = 60, RECON_GOOGLE_ICON = 30, RECON_SCAN_W = 84, RECON_SCAN_H = 64;
export const RECON_RESIDUAL_BUTTON_CSS = `
:where([data-rv-button])[data-rv-variant="recon-account"] {
  width: ${TOUCH_MIN}px;
  height: ${TOUCH_MIN}px;
  border-radius: 50%;
  border: 1px solid ${T.separatorBold};
  background: ${T.accentSoft};
  color: ${T.accentInk};
  font-family: ${T.fontDisplay};
  font-weight: ${weight.bold};
  font-size: ${font.footnote / font.headline}rem;
  display: grid;
  place-items: center;
  overflow: hidden;
  padding: 0;
}

:where([data-rv-button])[data-rv-variant="recon-account"] img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

button:where([data-rv-button])[data-rv-variant="recon-menu"] {
  width: 100%;
  min-height: ${TOUCH_MIN}px;
  text-align: left;
  padding: 0 ${space[3]}px;
  border-radius: ${radius.sm}px;
  border: 0;
  background: transparent;
  font-weight: ${weight.medium};
  transition: background ${RECON_HOVER_MS}ms ${T.ease};
}
@media (hover: hover){
button:where([data-rv-button])[data-rv-variant="recon-menu"]:hover {
    background: ${role('surface3')};
  }
}
:where([data-rv-button])[data-rv-variant="recon-surface"] {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${space[3]}px;
  min-height: 104px;
  padding: ${space[4]}px ${space[3]}px ${space[3]}px;
  border-radius: ${radius.card}px;
  border: 1px solid ${T.separator};
  background: ${T.surface};
  text-align: left;
  transition:
    transform ${RECON_HOVER_MS}ms ${T.ease},
    border-color ${RECON_HOVER_MS}ms ${T.ease},
    background ${RECON_HOVER_MS}ms ${T.ease};
}

:where([data-rv-button])[data-rv-variant="recon-surface"]:active {
  transform: scale(0.97);
}

:where([data-rv-button])[data-rv-variant="recon-surface"] > svg {
  width: 30px;
  height: 30px;
  color: ${T.labelSec};
  transition: color ${RECON_HOVER_MS}ms ${T.ease};
}

:where([data-rv-button])[data-rv-variant="recon-surface"] .t {
  font-weight: ${weight.semibold};
  font-size: ${font.headline / font.headline}rem;
  line-height: 1.15;
}

:where([data-rv-button])[data-rv-variant="recon-surface"] .s {
  font-size: ${font.caption / font.headline}rem;
  color: ${T.labelTer};
  line-height: 1.2;
}

:where([data-rv-button])[data-rv-variant="recon-surface"][aria-checked='true'] {
  border-color: ${T.accent};
  background: linear-gradient(160deg, ${role('wash-emerald-16')}, ${role('wash-emerald-05')});
  box-shadow:
    0 0 0 1px ${T.accent} inset,
    0 10px 28px ${role('wash-emerald-12')};
}

:where([data-rv-button])[data-rv-variant="recon-surface"][aria-checked='true'] > svg {
  color: ${T.accent};
}

:where([data-rv-button])[data-rv-variant="recon-surface"] .tick {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${T.accent};
  color: ${T.onAccent};
  display: grid;
  place-items: center;
  opacity: 0;
  transform: scale(0.6);
  transition:
    opacity ${RECON_HOVER_MS}ms ${T.ease},
    transform 250ms ${T.spring};
}

:where([data-rv-button])[data-rv-variant="recon-surface"][aria-checked='true'] .tick {
  opacity: 1;
  transform: none;
}
@media (max-width: 360px){
:where([data-rv-button])[data-rv-variant="recon-surface"] .s {
    display: none;
  }
}
:where([data-rv-button])[data-rv-variant="recon-audit-row"] {
  display: flex;
  gap: ${space[4]}px;
  align-items: center;
  padding: ${space[3]}px;
  min-height: 84px;
  border-radius: ${radius.card}px;
  border: 1px solid ${T.separator};
  background: ${role('card')};
  color: inherit;
  text-decoration: none;
  text-align: left;
  width: 100%;
  transition:
    border-color ${RECON_HOVER_MS}ms ${T.ease},
    transform ${RECON_HOVER_MS}ms ${T.ease},
    background ${RECON_HOVER_MS}ms ${T.ease};
  animation: rvReconRowIn 320ms ${T.ease} both;
}

:where([data-rv-button])[data-rv-variant="recon-audit-row"]:active {
  transform: scale(0.99);
}
@media (hover: hover){
:where([data-rv-button])[data-rv-variant="recon-audit-row"]:hover {
    border-color: ${T.separatorBold};
    background: ${T.surfaceAlt};
  }
}
button[data-rv-button][data-rv-variant="recon-photo"] {
  position: absolute;
  inset: 0;
  width: 100%;
  border: 0;
  background: transparent;
}

:where([data-rv-button])[data-rv-variant="recon-surface"]:active,
:where([data-rv-button])[data-rv-variant="recon-audit-row"]:active {
  transition-duration: ${RECON_PRESS_MS}ms;
}

:where([data-rv-button]):is([data-rv-variant="recon-entity"], [data-rv-variant="recon-table-label"]) {
  justify-self: start;
  min-height: ${TOUCH_MIN}px;
  margin: -8px 0 -8px -8px;
  padding: 0 ${space[2]}px;
  border: 0;
  background: none;
  color: ${T.labelTer};
  font-size: ${font.footnote / font.headline}rem;
  font-weight: ${weight.medium};
  text-decoration: underline;
  text-underline-offset: 3px;
}
@media (hover: hover){
:where([data-rv-button]):is([data-rv-variant="recon-entity"], [data-rv-variant="recon-table-label"]):hover {
    color: ${T.label};
  }
}
:where([data-rv-button])[data-rv-variant="recon-audit-row"].has-link {
  position: relative;
}

:where([data-rv-button])[data-rv-variant="recon-audit-row"] .row-link {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  z-index: 0;
}

:where([data-rv-button])[data-rv-variant="recon-audit-row"] .row-link:focus-visible {
  outline-offset: -2px;
}

:where([data-rv-button])[data-rv-variant="recon-audit-row"].has-link .thumb,
:where([data-rv-button])[data-rv-variant="recon-audit-row"].has-link .audit-body {
  pointer-events: none;
}

:where([data-rv-button])[data-rv-variant="recon-audit-row"] .store-link {
  position: relative;
  z-index: 1;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  min-height: ${TOUCH_MIN}px;
  margin: -12px 0 -12px;
  color: inherit;
  text-decoration: underline;
  text-decoration-color: ${T.separatorBold};
  text-underline-offset: 4px;
  text-decoration-thickness: 1.5px;
  border-radius: ${radius.sm}px;
}
@media (hover: hover){
:where([data-rv-button])[data-rv-variant="recon-audit-row"] .store-link:hover {
    color: ${T.accentInk};
    text-decoration-color: ${T.accent};
  }
}
:where([data-rv-button])[data-rv-variant="recon-invoice"] {
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${space[3]}px;
  min-height: 72px;
  padding: ${space[3]}px ${space[4]}px;
  border-radius: ${radius.card}px;
  border: 1px solid ${T.separator};
  background: ${role('card')};
  text-align: left;
  transition:
    border-color ${RECON_HOVER_MS}ms ${T.ease},
    background ${RECON_HOVER_MS}ms ${T.ease};
}

:where([data-rv-button])[data-rv-variant="recon-invoice"][aria-pressed='true'] {
  border-color: ${T.accent};
  box-shadow: 0 0 0 1px ${T.accent} inset;
}
@media (hover: hover){
:where([data-rv-button])[data-rv-variant="recon-invoice"]:hover {
    border-color: ${T.separatorBold};
    background: ${T.surfaceAlt};
  }
}

:where([data-rv-button])[data-rv-variant="recon-disclosure"] {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${TOUCH_MIN}px;
  margin: -12px 0;
  padding: 0;
  border: 0;
  background: none;
  text-align: left;
}

:where([data-rv-button])[data-rv-variant="recon-lock"] {
  flex: none;
}

:where([data-rv-button])[data-rv-variant="recon-lock"] {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${TOUCH_MIN}px;
  height: ${TOUCH_MIN}px;
  border-radius: ${radius.sm}px;
  border: 1px solid ${T.separator};
  background: ${T.surfaceAlt};
  color: ${T.labelSec};
}

:where([data-rv-button])[data-rv-variant="recon-lock"][aria-pressed='true'] {
  color: ${T.amberInk};
  border-color: color-mix(in srgb, ${T.amber} 55%, transparent);
  background: ${T.amberSoft};
}

:where([data-rv-button])[data-rv-variant="recon-sku"] {
  display: flex;
  align-items: center;
  gap: ${space[2]}px;
  min-height: ${TOUCH_MIN}px;
  padding: 0;
  border: 0;
  background: none;
  text-align: left;
  max-width: 220px;
}

:where([data-rv-button])[data-rv-variant="recon-sku"] .ps-chev {
  width: 7px;
  height: 7px;
  margin-left: 2px;
}


:where([data-rv-button])[data-rv-variant="recon-table-label"] { color: ${T.label}; text-decoration: none; font-weight: ${weight.semibold}; font-size: ${font.callout / font.headline}rem; margin: 0; text-align: left; }

@keyframes rvReconRowIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { :where([data-rv-button])[data-rv-variant="recon-audit-row"] { animation-duration: 0.01ms; animation-iteration-count: 1; transition-duration: 0.01ms; } }
:where([data-rv-button])[data-rv-variant="recon-google"] {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${space[3]}px;
  min-height: ${RECON_GOOGLE_H}px;
  border-radius: ${radius.card}px;
  border: 0;
  background: linear-gradient(180deg, color-mix(in srgb, ${T.accent} 86%, ${role('white')}) 0%, ${T.accent} 55%, color-mix(in srgb, ${T.accent} 88%, ${role('black')}) 100%);
  color: ${T.onAccent};
  font-weight: ${weight.bold};
  font-size: ${font.title3 / font.headline}rem;
  text-decoration: none;
  box-shadow: 0 12px 32px ${role('wash-emerald-32')};
  transition: transform ${RECON_HOVER_MS}ms ${T.ease};
}
:where([data-rv-button])[data-rv-variant="recon-google"]:active {
  transform: scale(0.98);
}
:where([data-rv-button])[data-rv-variant="recon-google"] .g {
  width: ${RECON_GOOGLE_ICON}px;
  height: ${RECON_GOOGLE_ICON}px;
  border-radius: ${radius.sm}px;
  background: ${role('white')};
  display: grid;
  place-items: center;
}
/* Secondary action next to Capture: invoice scan. */
:where([data-rv-button])[data-rv-variant="recon-scan"] {
  position: relative;
  flex: none;
  width: ${RECON_SCAN_W}px;
  min-height: ${RECON_SCAN_H}px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space[1]}px;
  border-radius: ${radius.card}px;
  border: 1px solid ${T.separatorBold};
  background: ${T.surfaceAlt};
  color: ${T.label};
  font-weight: ${weight.semibold};
  font-size: ${font.footnote / font.headline}rem;
  cursor: pointer;
  transition:
    transform ${RECON_HOVER_MS}ms ${T.ease},
    border-color ${RECON_HOVER_MS}ms ${T.ease},
    background ${RECON_HOVER_MS}ms ${T.ease};
}
:where([data-rv-button])[data-rv-variant="recon-scan"] svg {
  width: ${space[6]}px;
  height: ${space[6]}px;
  color: ${T.amberInk};
}
:where([data-rv-button])[data-rv-variant="recon-scan"]:active {
  transform: scale(0.97);
}
[data-rv-button][data-rv-variant="recon-scan"]:is(:focus-within, :focus-visible) {
  outline: 2px solid ${T.accent};
  outline-offset: 3px;
}
:where([data-rv-button])[data-rv-variant="recon-scan"].busy {
  opacity: 0.7;
  cursor: progress;
}
:where([data-rv-button])[data-rv-variant="recon-scan"].busy svg {
  animation: rvReconScanPulse 1s infinite;
}
@media (hover: hover) {
  :where([data-rv-button])[data-rv-variant="recon-scan"]:hover {
    border-color: ${T.accent};
    background: ${role('surface3')};
  }
}

:where([data-rv-button])[data-rv-variant="recon-site-header"] {
  display: inline-flex;
  align-items: center;
  min-height: ${TOUCH_MIN}px;
  padding: 0 ${space[4]}px;
  border-radius: ${radius.pill}px;
  font-weight: ${weight.semibold};
  font-size: ${font.callout / font.headline}rem;
  text-decoration: none;
  color: ${T.onAccent};
  background: ${T.accent};
  white-space: nowrap;
}
:where([data-rv-button])[data-rv-variant="recon-site-header"]:hover {
  filter: brightness(1.06);
}

:where([data-rv-button]):is([data-rv-variant="recon-site-primary"], [data-rv-variant="recon-site-ghost"]) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${space[6] * 2}px;
  padding: 0 ${space[5]}px;
  border-radius: ${radius.lg}px;
  font-weight: ${weight.semibold};
  font-size: ${font.headline / font.headline}rem;
  text-decoration: none;
  border: 1px solid transparent;
  transition:
    transform ${RECON_HOVER_MS}ms ${T.ease},
    background ${RECON_HOVER_MS}ms ${T.ease},
    border-color ${RECON_HOVER_MS}ms ${T.ease};
}
:where([data-rv-button]):is([data-rv-variant="recon-site-primary"], [data-rv-variant="recon-site-ghost"]):active {
  transform: scale(0.98);
}
:where([data-rv-button])[data-rv-variant="recon-site-primary"] {
  background: ${T.accent};
  color: ${T.onAccent};
}
:where([data-rv-button])[data-rv-variant="recon-site-ghost"] {
  border-color: ${T.separatorBold};
  color: ${T.label};
  background: ${T.surface};
}

:where([data-rv-button])[data-rv-variant="recon-google"]:active, :where([data-rv-button])[data-rv-variant="recon-scan"]:active, :where([data-rv-button]):is([data-rv-variant="recon-site-primary"], [data-rv-variant="recon-site-ghost"]):active { transition-duration: ${RECON_PRESS_MS}ms; }

@keyframes rvReconScanPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
`;
